'use client';

import { useState } from 'react';
import {
    TrendingUp,
    Filter,
    Search,
    ChevronDown,
    Calendar,
    Landmark,
    Briefcase,
    PieChart,
    Plus,
    Eye,
    X
} from 'lucide-react';
import Link from 'next/link';

export default function SetoranModalPage() {
    const schoolName = "PAUD Al-Azhar 1";
    const [searchTerm, setSearchTerm] = useState('');
    const [dateFilter, setDateFilter] = useState<'all' | 'thisYear'>('thisYear');
    const [typeFilter, setTypeFilter] = useState<'all' | 'Modal Tunai' | 'Hibah Terikat' | 'Modal Kerja'>('all');
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string | null>(null);

    // Mock Data Setoran Modal
    const capitals = [
        {
            id: 'MOD-2024-001',
            date: '2024-01-05',
            description: 'Setoran Modal Awal Tahun Ajaran 2024/2025',
            source: 'Yayasan Pusat',
            amount: 'Rp 250.000.000',
            type: 'Modal Tunai',
            status: 'Verified',
            bankAccount: 'Bank Mandiri - Operasional'
        },
        {
            id: 'MOD-2024-002',
            date: '2024-01-15',
            description: 'Tambahan Modal Pengembangan Sarana Bermain',
            source: 'Donatur Tetap',
            amount: 'Rp 75.000.000',
            type: 'Hibah Terikat',
            status: 'Verified',
            bankAccount: 'BSI - Pembangunan'
        },
        {
            id: 'MOD-2023-088',
            date: '2023-12-20',
            description: 'Suntikan Dana Operasional Akhir Tahun',
            source: 'Yayasan Pusat',
            amount: 'Rp 50.000.000',
            type: 'Modal Kerja',
            status: 'Verified',
            bankAccount: 'Bank Riau Kepri - Kas'
        },
        // Old data
        {
            id: 'MOD-2022-045',
            date: '2022-06-12',
            description: 'Modal Pendirian Unit Baru',
            source: 'Yayasan Pusat',
            amount: 'Rp 500.000.000',
            type: 'Modal Tunai',
            status: 'Verified',
            bankAccount: 'Bank Mandiri - Operasional'
        }
    ];

    const filteredCapitals = capitals.filter(item => {
        const matchesSearch = item.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.source.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesDate = dateFilter === 'all' || (dateFilter === 'thisYear' && parseInt(item.date.split('-')[0]) >= 2023); // Considering 2023 as relatively current for this context
        const matchesType = typeFilter === 'all' || item.type === typeFilter;

        return matchesSearch && matchesDate && matchesType;
        return matchesSearch && matchesDate && matchesType;
    });

    const handleDetailClick = (id: string) => {
        setSelectedId(id);
        setIsDetailModalOpen(true);
    };

    return (
        <main className="p-4 md:p-10 space-y-8 max-w-7xl mx-auto w-full pb-32 animate-in fade-in duration-700">
            {/* Header Area */}
            <section className="bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden">
                <div className="absolute top-0 right-0 w-96 h-96 bg-primary-soft/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none"></div>

                <div className="relative z-10 space-y-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div className="space-y-4">
                            <div className="flex items-center gap-2 text-[10px] font-bold text-muted uppercase tracking-widest">
                                <Link href="/sekolah" className="hover:text-primary transition-all">Keuangan</Link>
                                <span className="text-muted/50">•</span>
                                <span className="text-foreground">Setoran Modal</span>
                            </div>

                            <div className="flex items-center gap-6">
                                <div className="w-16 h-16 rounded-2xl bg-primary-soft/50 border border-primary/10 flex items-center justify-center text-primary shadow-inner">
                                    <TrendingUp className="w-8 h-8" />
                                </div>
                                <div className="space-y-1">
                                    <h1 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight whitespace-nowrap">Setoran Modal</h1>
                                    <p className="text-secondary font-medium flex items-center gap-2">
                                        <Landmark className="w-3.5 h-3.5 text-primary" /> Ekuitas & Investasi • {schoolName}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <Link href="/sekolah/setoran-modal/tambah">
                            <button className="flex items-center gap-3 px-6 py-3.5 bg-primary text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-primary/20 hover:scale-[1.02] transition-all">
                                <Plus className="w-4 h-4" /> Input Modal Baru
                            </button>
                        </Link>
                    </div>
                </div>
            </section>

            {/* Stats Summary */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-primary-soft/30 rounded-2xl flex items-center justify-center text-primary">
                        <Briefcase className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Modal Disetor (2024)</p>
                        <p className="text-xl font-black text-foreground">Rp 325.000.000</p>
                    </div>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-info-soft/30 rounded-2xl flex items-center justify-center text-info">
                        <PieChart className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Kontribusi Yayasan</p>
                        <p className="text-xl font-black text-foreground">77%</p>
                    </div>
                </div>
                <div className="bg-surface p-6 rounded-3xl border border-border-main shadow-sm flex items-center gap-5">
                    <div className="w-12 h-12 bg-success-soft/30 rounded-2xl flex items-center justify-center text-success">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-[10px] font-bold text-muted uppercase tracking-widest">Pertumbuhan Modal</p>
                        <p className="text-xl font-black text-foreground">+12.5%</p>
                    </div>
                </div>
            </div>

            {/* Filter & Search */}
            <div className="bg-surface border border-border-main p-4 rounded-3xl shadow-sm space-y-4">
                <div className="flex flex-col md:flex-row gap-4">
                    {/* Search */}
                    <div className="relative flex-1 group w-full md:max-w-xs">
                        <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted group-focus-within:text-primary transition-colors" />
                        <input
                            type="text"
                            placeholder="Cari ID / penyetor..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full bg-background/50 border border-border-main rounded-2xl py-3.5 pl-12 pr-6 text-xs font-semibold focus:ring-4 focus:ring-primary/5 focus:border-primary/20 transition-all outline-none"
                        />
                    </div>

                    {/* Filters Group */}
                    <div className="flex flex-wrap gap-3 flex-1">
                        {/* Account Filter */}
                        <div className="relative group min-w-[150px]">
                            <select
                                value={typeFilter}
                                onChange={(e) => setTypeFilter(e.target.value as any)}
                                className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted"
                            >
                                <option value="all">Semua Tipe Modal</option>
                                <option value="Modal Tunai">Modal Tunai</option>
                                <option value="Hibah Terikat">Hibah Terikat</option>
                                <option value="Modal Kerja">Modal Kerja</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        {/* Status Filter */}
                        <div className="relative group min-w-[150px]">
                            <select className="w-full pl-4 pr-10 py-3.5 bg-background border border-border-main rounded-2xl text-[10px] font-bold uppercase tracking-widest outline-none focus:ring-4 focus:ring-primary/5 focus:border-primary/30 appearance-none cursor-pointer hover:border-primary/30 transition-all text-muted">
                                <option value="">Semua Status</option>
                                <option value="verified">Verified</option>
                                <option value="pending">Pending</option>
                            </select>
                            <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted pointer-events-none" />
                        </div>

                        {/* Date Range Picker Placeholder */}
                        <button
                            onClick={() => setDateFilter(prev => prev === 'all' ? 'thisYear' : 'all')}
                            className={`flex items-center gap-3 px-5 py-3.5 border rounded-2xl text-[10px] font-bold uppercase tracking-widest transition-all ${dateFilter === 'thisYear'
                                ? 'bg-primary text-white border-primary shadow-lg shadow-primary/20'
                                : 'bg-background border-border-main text-muted hover:border-primary/30'
                                }`}
                        >
                            <Calendar className="w-4 h-4" />
                            {dateFilter === 'thisYear' ? 'Tahun Ini' : 'Pilih Tanggal'}
                        </button>
                    </div>
                </div>
            </div>

            {/* Transactions Table */}
            <div className="bg-surface border border-border-main rounded-3xl overflow-hidden shadow-sm">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-background/50 border-b border-border-main">
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px]">ID & Tanggal</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px]">Sumber Dana</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[180px] text-right">Jumlah</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[160px] text-center">Rekening Tujuan</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[120px] text-center">Tipe</th>
                                <th className="px-6 py-5 text-[10px] font-bold text-muted uppercase tracking-widest w-[80px] text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border-main/50">
                            {filteredCapitals.length > 0 ? filteredCapitals.map((item, idx) => (
                                <tr key={idx} className="hover:bg-primary-soft/5 transition-colors group">
                                    <td className="px-6 py-4">
                                        <div>
                                            <p className="text-xs font-black text-primary group-hover:underline">{item.id}</p>
                                            <div className="flex items-center gap-1.5 mt-1 text-muted">
                                                <Calendar className="w-3 h-3" />
                                                <span className="text-[10px] font-bold uppercase">{item.date}</span>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4">
                                        <p className="text-sm font-bold text-foreground line-clamp-2">{item.description}</p>
                                    </td>
                                    <td className="px-6 py-4">
                                        <div className="flex items-center gap-2">
                                            <div className="w-6 h-6 rounded-full bg-surface border border-border-main flex items-center justify-center text-[10px] font-bold text-primary">
                                                {item.source.charAt(0)}
                                            </div>
                                            <span className="text-xs font-bold text-secondary">{item.source}</span>
                                        </div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <p className="text-sm font-black text-primary tabular-nums">{item.amount}</p>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="text-[10px] font-bold text-muted uppercase">{item.bankAccount}</span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-info-soft/20 text-info border border-info/10 text-[9px] font-bold uppercase tracking-wide">
                                            {item.type}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 text-center">
                                        <button
                                            onClick={() => handleDetailClick(item.id)}
                                            className="p-2 rounded-xl text-muted hover:text-primary hover:bg-primary-soft/30 transition-all"
                                        >
                                            <Eye className="w-4 h-4" />
                                        </button>
                                    </td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-10 text-center text-muted font-medium">
                                        Tidak ada data yang ditemukan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
            {/* Detail Modal */}
            {isDetailModalOpen && selectedId && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-surface p-8 rounded-3xl shadow-2xl max-w-2xl w-full space-y-8 animate-in zoom-in-95 duration-200 relative">
                        <button
                            onClick={() => setIsDetailModalOpen(false)}
                            className="absolute top-6 right-6 p-2 rounded-full hover:bg-secondary-soft/50 text-muted hover:text-foreground transition-all"
                        >
                            <X className="w-5 h-5" />
                        </button>

                        <div className="flex items-center gap-4 border-b border-border-main pb-6">
                            <div className="w-14 h-14 bg-primary-soft/30 rounded-2xl flex items-center justify-center text-primary">
                                <TrendingUp className="w-7 h-7" />
                            </div>
                            <div>
                                <h3 className="text-xl font-black text-foreground">Detail Setoran Modal</h3>
                                <p className="text-sm text-muted font-medium">#{selectedId}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Tanggal Transaksi</label>
                                <p className="text-sm font-bold text-foreground">{capitals.find(r => r.id === selectedId)?.date}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Tipe Modal</label>
                                <div className="flex">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-lg bg-info-soft/20 text-info border border-info/10 text-[10px] font-bold uppercase tracking-wide">
                                        {capitals.find(r => r.id === selectedId)?.type}
                                    </span>
                                </div>
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Sumber Dana</label>
                                <p className="text-sm font-bold text-foreground">{capitals.find(r => r.id === selectedId)?.source}</p>
                            </div>
                            <div className="space-y-1 md:col-span-2">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Keterangan</label>
                                <p className="text-sm font-medium text-foreground bg-background/50 p-4 rounded-xl border border-border-main">
                                    {capitals.find(r => r.id === selectedId)?.description}
                                </p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Rekening Tujuan</label>
                                <p className="text-sm font-bold text-foreground">{capitals.find(r => r.id === selectedId)?.bankAccount}</p>
                            </div>
                            <div className="space-y-1">
                                <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Status</label>
                                <p className="text-sm font-bold text-success">{capitals.find(r => r.id === selectedId)?.status}</p>
                            </div>

                            <div className="space-y-1 md:col-span-2 pt-4 border-t border-border-main flex items-center justify-between">
                                <div>
                                    <label className="text-[10px] font-bold text-muted uppercase tracking-widest">Total Nominal</label>
                                    <p className="text-2xl font-black text-primary tabular-nums">{capitals.find(r => r.id === selectedId)?.amount}</p>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-3 pt-2">
                            <button
                                onClick={() => setIsDetailModalOpen(false)}
                                className="w-full px-4 py-3 bg-secondary-soft/20 text-secondary hover:bg-secondary-soft/40 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
                            >
                                Tutup Detail
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </main>
    );
}
